import React, { createContext, useContext, useMemo, useState } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';

const initialConfig = {
  namespace: 'StickyNoteEditor',
  onError: (error: Error) => {
    console.error('Lexical error:', error);
  },
  nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode],
};

interface RichTextEditorCtxType {
  urlEditorOpen: boolean;
  setUrlEditorOpen: (open: boolean) => void;
}

export const RichTextEditorCtx = createContext<RichTextEditorCtxType>({
  urlEditorOpen: false,
  setUrlEditorOpen: () => {},
});

export const RichTextEditorProvider = ({ children }: { children: React.ReactNode }) => {
  const [urlEditorOpen, setUrlEditorOpen] = useState(false);
  return (
    <RichTextEditorCtx.Provider value={{ urlEditorOpen, setUrlEditorOpen }}>
      <LexicalComposer initialConfig={initialConfig}>{children}</LexicalComposer>
    </RichTextEditorCtx.Provider>
  );
};

export const useRichTextEditor = () => {
  const [editor] = useLexicalComposerContext();
  const { urlEditorOpen, setUrlEditorOpen } = useContext(RichTextEditorCtx);

  if (!editor) {
    throw new Error('useRichTextEditor must be used within a RichTextEditorProvider');
  }

  return useMemo(
    () => ({ editor, urlEditorOpen, setUrlEditorOpen }),
    [editor, urlEditorOpen, setUrlEditorOpen],
  );
};
